import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import {
  fetchRoutingConfig,
  createRoutingConfig,
  updateRoutingConfig,
  deleteRoutingConfig,
  fetchRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  triggerRoutingSync,
  clearCallRouting,
  selectCallRouting,
} from '../../store/slices/callRoutingSlice';
import {
  CreateRoutingConfigPayload,
  CreateRoutingRulePayload,
  UpdateRoutingConfigPayload,
  UpdateRoutingRulePayload,
} from '../../types/phone-numbers';
import { RootState } from '../../store';
import { logger } from '../../utils/logger';

export const useCallRouting = (phoneNumberId: number) => {
  const dispatch = useDispatch<AppDispatch>();
  const clientId = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation?.id ?? null
  );
  const {
    routingConfig,
    routingRules,
    isLoadingConfig,
    isLoadingRules,
    isSavingConfig,
    isSavingRule,
    error,
  } = useSelector(selectCallRouting);

  const fetchConfig = useCallback(async () => {
    if (!clientId) return;
    try {
      await dispatch(fetchRoutingConfig({ organisationId: clientId, phoneNumberId })).unwrap();
    } catch (err) {
      logger.error('useCallRouting: fetchConfig failed', err);
      throw err;
    }
  }, [dispatch, clientId, phoneNumberId]);

  const saveConfig = useCallback(
    async (payload: CreateRoutingConfigPayload | UpdateRoutingConfigPayload) => {
      if (!clientId) return;
      try {
        if (routingConfig) {
          await dispatch(
            updateRoutingConfig({ organisationId: clientId, phoneNumberId, payload })
          ).unwrap();
        } else {
          await dispatch(
            createRoutingConfig({ organisationId: clientId, phoneNumberId, payload })
          ).unwrap();
        }
      } catch (err) {
        logger.error('useCallRouting: saveConfig failed', err);
        throw err;
      }
    },
    [dispatch, clientId, phoneNumberId, routingConfig]
  );

  const removeConfig = useCallback(async () => {
    if (!clientId) return;
    try {
      await dispatch(deleteRoutingConfig({ organisationId: clientId, phoneNumberId })).unwrap();
    } catch (err) {
      logger.error('useCallRouting: removeConfig failed', err);
      throw err;
    }
  }, [dispatch, clientId, phoneNumberId]);

  const fetchRules = useCallback(async () => {
    if (!clientId) return;
    try {
      await dispatch(fetchRoutingRules({ organisationId: clientId, phoneNumberId })).unwrap();
    } catch (err) {
      logger.error('useCallRouting: fetchRules failed', err);
      throw err;
    }
  }, [dispatch, clientId, phoneNumberId]);

  const addRule = useCallback(
    async (payload: CreateRoutingRulePayload) => {
      if (!clientId) return;
      try {
        await dispatch(
          createRoutingRule({ organisationId: clientId, phoneNumberId, payload })
        ).unwrap();
      } catch (err) {
        logger.error('useCallRouting: addRule failed', err);
        throw err;
      }
    },
    [dispatch, clientId, phoneNumberId]
  );

  const editRule = useCallback(
    async (ruleId: number, payload: UpdateRoutingRulePayload) => {
      if (!clientId) return;
      try {
        await dispatch(updateRoutingRule({ organisationId: clientId, ruleId, payload })).unwrap();
      } catch (err) {
        logger.error('useCallRouting: editRule failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const removeRule = useCallback(
    async (ruleId: number) => {
      if (!clientId) return;
      try {
        await dispatch(deleteRoutingRule({ organisationId: clientId, ruleId })).unwrap();
      } catch (err) {
        logger.error('useCallRouting: removeRule failed', err);
        throw err;
      }
    },
    [dispatch, clientId]
  );

  const triggerSync = useCallback(async () => {
    if (!clientId) return;
    try {
      await dispatch(triggerRoutingSync({ organisationId: clientId, phoneNumberId })).unwrap();
    } catch (err) {
      logger.error('useCallRouting: triggerSync failed', err);
      throw err;
    }
  }, [dispatch, clientId, phoneNumberId]);

  const clear = useCallback(() => {
    dispatch(clearCallRouting());
  }, [dispatch]);

  return {
    routingConfig,
    routingRules,
    isLoadingConfig,
    isLoadingRules,
    isSavingConfig,
    isSavingRule,
    error,
    fetchConfig,
    saveConfig,
    removeConfig,
    fetchRules,
    addRule,
    editRule,
    removeRule,
    triggerSync,
    clear,
  };
};
